"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/*
 * Copyright 2023, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const react_1 = __importStar(require("react"));
const react_redux_1 = require("react-redux");
const react_i18next_1 = require("react-i18next");
const moment_1 = __importDefault(require("moment"));
const get_1 = __importDefault(require("lodash/get"));
const project_config_1 = __importDefault(require("chaire-lib-common/lib/config/shared/project.config"));
const application_config_1 = __importDefault(require("../../config/application.config"));
const surveyHelper = __importStar(require("evolution-common/lib/utils/helpers"));
const Section_1 = __importDefault(require("../pageParts/Section"));
const SectionNav_1 = __importDefault(require("../pageParts/SectionNav"));
const ConfirmModal_1 = __importDefault(require("chaire-lib-frontend/lib/components/modal/ConfirmModal"));
const LoadingPage_1 = __importDefault(require("chaire-lib-frontend/lib/components/pages/LoadingPage"));
const utils_1 = require("../../actions/utils");
const WithPreferencesHoc_1 = require("../hoc/WithPreferencesHoc");
const errorHandling_1 = require("../../services/errorManagement/errorHandling");
const errorHandling_2 = require("../../services/errorManagement/errorHandling");
const SurveyContext_1 = require("../../contexts/SurveyContext");
const Survey = (props) => {
    // Prepare survey state
    const [confirmCompleteBeforeSwitchingOpened, setConfirmCompleteBeforeSwitchingOpened] = react_1.default.useState(false);
    // Get state selectors and other hooks
    const { t, i18n } = (0, react_i18next_1.useTranslation)(['survey', 'main']);
    const { errors, submitted, navigation } = (0, react_redux_1.useSelector)((state) => state.survey);
    const user = (0, react_redux_1.useSelector)((state) => state.auth.user);
    const loadingState = (0, react_redux_1.useSelector)((state) => state.loadingState.loadingState);
    const { sections, appContext } = (0, react_1.useContext)(SurveyContext_1.SurveyContext);
    react_1.default.useEffect(() => {
        // set language if empty or unavailable and change locale:
        if (!i18n.language || project_config_1.default.languages.indexOf(i18n.language) <= -1) {
            i18n.changeLanguage(project_config_1.default.defaultLocale);
        }
        moment_1.default.locale(i18n.language);
        // Override the window.console functions to log errors and warnings to the server. It is here because it's the top most component with an interview to associate with
        (0, errorHandling_1.overrideConsoleLogs)({ getInterviewId: () => props.interview.id });
        return () => {
            // Reset the console functions to their original values
            (0, errorHandling_2.restoreConsoleLogs)();
        };
    }, []);
    react_1.default.useEffect(() => {
        // Set the background logo image to the style properties
        if (project_config_1.default.logoPaths) {
            document.documentElement.style.setProperty('--dynamic-logo-image', `url(${project_config_1.default.logoPaths[i18n.language]})`);
        }
    }, [i18n.language]);
    const onChangeSection = (targetSection, activeSection, allWidgetsValid, e) => {
        if (e) {
            e.preventDefault();
        }
        if (activeSection === targetSection) {
            return null;
        }
        if (allWidgetsValid) {
            // Navigate to the requested section
            props.startNavigate({ requestedSection: { sectionShortname: targetSection } });
        }
        else {
            setConfirmCompleteBeforeSwitchingOpened(true);
            setTimeout(() => {
                console.log('should scroll to first invalid question after rendering');
                const scrollPosition = (0, get_1.default)(document.getElementsByClassName('question-invalid'), '[0].offsetTop', null);
                if (scrollPosition && scrollPosition >= 0) {
                    window.scrollTo(0, scrollPosition);
                }
            }, 0);
        }
    };
    const closeConfirmModal = (e) => {
        if (e) {
            e.preventDefault();
        }
        setConfirmCompleteBeforeSwitchingOpened(false);
    };
    const allWidgetsValid = (0, utils_1.validateAllWidgets)(props.interview);
    const activeSection = navigation && navigation.currentSection ? navigation.currentSection.sectionShortname : null;
    // FIXME If the sectionLoaded is not the same as the active one, is something somewhere loading it right now? Or should we do something?
    // FIXME Consider using react Suspense instead of this logic for the loading page
    // FIXME Consider putting the LoadingPage on the section only, and displaying the SectionNav, in case there's a bug and one can navigate to a section with the menu
    if (!activeSection || props.interview.sectionLoaded !== activeSection) {
        surveyHelper.devLog('%c rendering empty survey', 'background: rgba(0,0,0,0.1);');
        return react_1.default.createElement(LoadingPage_1.default, null);
    }
    surveyHelper.devLog(`%c rendering survey with activeSection ${activeSection}`, 'background: rgba(0,0,0,0.2);');
    const sectionShortname = activeSection;
    const sectionConfig = sections[sectionShortname];
    // Determine the section component to use based on the section configuration
    let SectionComponent = Section_1.default;
    if (sectionConfig.template) {
        if (application_config_1.default.templateMapping[sectionConfig.template]) {
            SectionComponent = application_config_1.default.templateMapping[sectionConfig.template];
        }
        else {
            console.error(`Template ${sectionConfig.template} not found in appConfig.templateMapping`);
        }
    }
    let customStyle = {};
    if (activeSection && sections[activeSection] && sections[activeSection].customStyle) {
        customStyle = sections[activeSection].customStyle;
    }
    const surveyContainerClassNames = `survey${project_config_1.default.logoPaths ? ' survey-with-logo' : ''}`;
    return (react_1.default.createElement("div", { className: surveyContainerClassNames },
        react_1.default.createElement("div", { style: { width: '100%', margin: '0 auto' } },
            react_1.default.createElement(SectionNav_1.default, { sections: sections, onChangeSection: onChangeSection, activeSection: activeSection, interview: props.interview, allWidgetsValid: project_config_1.default.allowChangeSectionWithoutValidation ? true : allWidgetsValid, user: user }),
            confirmCompleteBeforeSwitchingOpened && (react_1.default.createElement("div", null,
                react_1.default.createElement(ConfirmModal_1.default, { title: '' /* TODO/FIXME: should we use a title? It was omitted in legacy. */, isOpen: true, closeModal: closeConfirmModal, text: t('main:completeSectionBeforeSwitching'), showCancelButton: true, showConfirmButton: false, cancelButtonLabel: t('main:OK'), cancelButtonColor: 'blue', containsHtml: false }))),
            react_1.default.createElement("form", { onSubmit: (e) => e.preventDefault(), className: "apptr__form", id: "survey_form", style: customStyle, autoComplete: "off" },
                react_1.default.createElement(SectionComponent, Object.assign({}, props, { key: sectionShortname, loadingState: loadingState, shortname: sectionShortname, sectionConfig: sectionConfig, 
                    // FIXME See the type of errors, there's a lang extra object in the state, but not in the component
                    errors: errors, user: user, submitted: submitted }))),
            react_1.default.createElement("div", { dangerouslySetInnerHTML: { __html: t(['survey:footer', 'main:footer'], { context: appContext }) } }))));
};
exports.default = (0, WithPreferencesHoc_1.withPreferencesHOC)(Survey);
//# sourceMappingURL=Survey.js.map